import React from "react";
import { Link, useLocation } from "react-router-dom";
import Drawer from "react-modern-drawer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { isMobile } from "react-device-detect";

import MidBox from "../../../components/Boxes/MidBox";
import MenuLanguagePicker from "../../../components/MenuLanguagePicker";
import useToast from "../../../hooks/useToast";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

interface DrawerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo: any;
  handleLogout: () => void;
}

const DrawerMenu: React.FC<DrawerMenuProps> = ({ 
  isOpen, 
  onClose, 
  userInfo, 
  handleLogout 
}) => {
  const openToast = useToast();
  const location = useLocation();
  const { t } = useAppTranslation(["navigation", "toasts", "common", "wheel"]);

  const drawerActions = [
    {
      image: "/assets/images/wheel/events/wheel-icon.png",
      title: t("wheel:menuLabel"),
      link: "#events",
    },
    {
      image: "/assets/images/icons/referral.svg",
      title: t("navigation:referralProgram"),
      link: "/referrals",
    },
    {
      image: "/assets/images/icons/inventory.svg",
      title: t("navigation:accountDetails"),
      link: "/profile",
    },
    {
      image: "/assets/images/icons/dao.svg",
      title: t("navigation:dao"),
      link: "/dao",
    },
    {
      image: "/assets/images/icons/tx.svg",
      title: t("navigation:transactions"),
      link: "/transaction-history",
    },
    {
      image: "/assets/images/icons/withdraw-tx.svg",
      title: t("navigation:withdrawHistory"),
      link: "/withdraw-history",
    },
    {
      image: "/assets/images/icons/settings.svg",
      title: t("navigation:settings"),
      link: "#settings",
    },
    {
      image: "/assets/images/icons/withdraw-tx.svg",
      title: t("navigation:nftBox"),
      link: "/mint-lands",
    },
    {
      image: "/assets/images/icons/edu.png",
      title: t("navigation:learn"),
      link: "/learn",
    },
    {
      image: "/assets/images/icons/support.png",
      title: t("navigation:support"),
      link: "https://support.chainoflegends.com",
    },
  ];

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="left"
      size={isMobile ? 300 : 350}
      // Above the map HUD (z-index 1030) so the menu is never covered by the
      // fixed top / bottom bars. Matches the chat drawer's z-index.
      zIndex={1089}
      className="bg-transparent"
    >
      <MidBox
        style={{ minHeight: "100%" }}
        title="d"
        noTitle
        containerClass="px-lg-4"
      >
        <button onClick={onClose} className="button hard btn-close"></button>
        <PerfectScrollbar style={{ height: "90vh", marginTop: -30 }}>
          <div className="px-0 px-lg-3">
            {userInfo && (
              <>
                <div className="d-flex justify-content-between align-items-center rounded-10 px-4 py-2 mb-2">
                  <div className="fs-big dark-yellow ">
                    {userInfo?.walletAddress?.slice(0, 4)}...
                    {userInfo?.walletAddress?.slice(
                      userInfo?.walletAddress?.length - 4,
                      userInfo?.walletAddress?.length
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-center">
                    <CopyToClipboard
                      text={userInfo?.walletAddress}
                      onCopy={() =>
                        openToast({
                          ok: true,
                          data: { message: t("toasts:walletCopied") },
                        })
                      }
                    >
                      <img
                        width={60}
                        className="button"
                        src="/assets/images/buttons/copy-btn-1.png"
                        alt={t("common:actions.copy")}
                      />
                    </CopyToClipboard>
                  </div>
                </div>
                <div className="mt-2 mt-md-2 dark-bg rounded-10 px-4 py-2 mb-4">
                  <div className="fs-small">{t("navigation:balance")}</div>
                  <div className="fs-big dark-yellow bold">
                    {userInfo?.totalCleg} Cleg
                  </div>
                </div>
              </>
            )}
            <hr />
            <div className="mt-2 d-flex flex-column">
              {drawerActions.map((item, index) =>
                item.link?.includes("https") ? (
                  <a
                    onClick={onClose}
                    target="_blank"
                    href={item.link}
                    key={index + "oxw"}
                    className="d-flex mt-2 row-item align-items-center"
                    rel="noopener noreferrer"
                  >
                    <img 
                      src={item.image} 
                      style={{ width: 26, height: 26 }} 
                      alt={item.title}
                    />
                    <span className="ms-2 dark-yellow">{item.title}</span>
                  </a>
                ) : (
                  <React.Fragment key={index + "oxw"}>
                  <Link
                    onClick={onClose}
                    to={
                      item.link.startsWith("#")
                        ? {
                            pathname: location.pathname,
                            search: location.search,
                            hash: item.link,
                          }
                        : item.link
                    }
                    className="d-flex mt-2 row-item align-items-center"
                  >
                    <img
                      src={item.image}
                      style={{ width: 26, height: 26 }}
                      alt={item.title}
                    />
                    <span className="ms-2 dark-yellow">{item.title}</span>
                  </Link>
                  {/* Language sits under Settings; the Settings dialog has it too. */}
                  {item.link === "#settings" && <MenuLanguagePicker />}
                  </React.Fragment>
                )
              )}
            </div>
            <hr />
            <div
              onClick={() => {
                onClose();
                handleLogout();
              }}
              className="d-flex mt-2 pointer w-100 text-center align-items-center"
            >
              <span className="text-center w-100 text-danger">{t("common:actions.logout")}</span>
            </div>
          </div>
        </PerfectScrollbar>
      </MidBox>
    </Drawer>
  );
};

export default DrawerMenu; 
